import { Log, LogCategory, Operation as LogOperation } from '@augloop/logging';
import { openDB } from 'idb';
import * as crossFetch from 'cross-fetch';
var fetchResource = (typeof fetch !== 'undefined') ? fetch : crossFetch;
var ResourceReturnType;
(function (ResourceReturnType) {
    ResourceReturnType["URL"] = "URL";
    ResourceReturnType["ArrayBuffer"] = "ArrayBuffer";
})(ResourceReturnType || (ResourceReturnType = {}));
var ModelDownloader = /** @class */ (function () {
    function ModelDownloader(hostApp, baseURL, options) {
        var _this = this;
        this.dbName = 'ALModels_db';
        this.osName = 'ALModels_os';
        this.dbVersion = 1;
        this.baseURL = "https://".concat(baseURL, "/web/").concat(hostApp);
        this.checkIdbByFeatures = options === null || options === void 0 ? void 0 : options.checkIdbByFeatures;
        // Start log operation
        var updateMetadataOp = new LogOperation({
            operationName: 'CreateModelDownloader',
            success: true,
            resultDescription: ''
        });
        updateMetadataOp.start();
        this.isBrowserIdbCompatible().then(function (canUseIdb) {
            if (!canUseIdb) {
                updateMetadataOp.resultDescription = 'Incompatible browser for IDB';
                Log.info(0x201c0142 /* tag_6hafc */, LogCategory.CoreDefault, updateMetadataOp.stop());
            }
            else {
                var timesUpdated_1 = 0;
                var timesDeleted_1 = 0;
                var timesCursorIncreased_1 = 0;
                var id_1;
                var timeoutPromise = new Promise(function (_resolve, reject) {
                    var ms = 10000;
                    id_1 = setTimeout(function () {
                        reject(new Error("Took longer than ".concat(ms, " ms")));
                    }, ms);
                });
                var updateTimesSinceUsagePromise = _this.accessDB().then(function (db) {
                    var transaction = db.transaction(_this.osName, 'readwrite');
                    return transaction.objectStore(_this.osName).openCursor().then(function updateTimes(cursor) {
                        timesCursorIncreased_1++;
                        if (!cursor) {
                            return transaction.done;
                        }
                        else {
                            var model = cursor.value;
                            if (!model.timesSinceUsage) {
                                model.timesSinceUsage = 0;
                            }
                            model.timesSinceUsage++;
                            var actionPromise = void 0;
                            if (model.timesSinceUsage > 5) {
                                // Delete unused models
                                timesDeleted_1++;
                                actionPromise = cursor.delete();
                            }
                            else {
                                // Update number of sessions since model was used
                                timesUpdated_1++;
                                actionPromise = cursor.update(model);
                            }
                            // Wait for current action to be complete before moving to next item in db
                            return actionPromise.then(function () {
                                return cursor.continue();
                            }).then(updateTimes);
                        }
                    });
                });
                _this.initPromise = Promise.race([timeoutPromise, updateTimesSinceUsagePromise]).catch(function (err) {
                    updateMetadataOp.success = false;
                    updateMetadataOp.resultDescription += "".concat(err.message, " ");
                }).then(function () {
                    updateMetadataOp.dimension0 = "".concat(timesCursorIncreased_1);
                    updateMetadataOp.dimension1 = "".concat(timesUpdated_1);
                    updateMetadataOp.dimension2 = "".concat(timesDeleted_1);
                    clearTimeout(id_1);
                    _this.closeDBAndEndLog(updateMetadataOp, "initializing idb storage");
                });
            }
        }).catch(function () {
            updateMetadataOp.resultDescription = 'Issue determining idb compatibility';
            Log.info(0x20017718 /* tag_6ax2y */, LogCategory.CoreDefault, updateMetadataOp.stop());
        });
    }
    ModelDownloader.prototype.getResourceAsArrayBuffer = function (modelId, version) {
        // Start log operation
        var getModelABOp = new LogOperation({
            operationName: 'GetResource',
            success: true,
            resultDescription: '',
            dimension0: ResourceReturnType.ArrayBuffer,
            dimension1: modelId,
            dimension2: version,
        });
        getModelABOp.start();
        var dict = {};
        return this.getResource(modelId, version, ResourceReturnType.ArrayBuffer, getModelABOp, function (model) {
            return Promise.all(Array.from(model.keys()).map(function (key) {
                var value = model.get(key);
                if (value.arrayBuffer) {
                    return value.arrayBuffer().then(function (abValue) {
                        dict[key] = abValue;
                    });
                }
                else {
                    return new Promise(function (resolve, reject) {
                        var reader = new FileReader();
                        reader.readAsArrayBuffer(value);
                        reader.onload = function (event) {
                            var abVal = event.target.result;
                            if (!(abVal instanceof ArrayBuffer)) {
                                reject(new Error('Unable to obtain ArrayBuffer'));
                            }
                            else {
                                dict[key] = abVal;
                                resolve();
                            }
                        };
                    });
                }
            }));
        }).then(function () {
            return dict;
        });
    };
    ModelDownloader.prototype.getResourceAsURL = function (modelId, version) {
        // Start log operation
        var getModelURLOp = new LogOperation({
            operationName: 'GetResource',
            success: true,
            resultDescription: '',
            dimension0: ResourceReturnType.URL,
            dimension1: modelId,
            dimension2: version
        });
        getModelURLOp.start();
        var dict = {};
        return this.getResource(modelId, version, ResourceReturnType.URL, getModelURLOp, function (model) {
            return Promise.all(Array.from(model.keys()).map(function (key) {
                // Translate blob to resourcePath
                dict[key] = URL.createObjectURL(model.get(key));
            }));
        }).then(function () {
            return dict;
        });
    };
    ModelDownloader.prototype.getResource = function (modelId, version, type, logOp, cb) {
        var _this = this;
        return this.getIndexedDBFiles(modelId, version, logOp)
            .then(function (model) {
            return cb(model);
        }).then(function () {
            _this.closeDBAndEndLog(logOp, "retrieving ".concat(modelId));
        }).catch(function (err) {
            if (logOp.resultDescription !== "".concat(err.message, " ")) {
                logOp.success = false;
                logOp.resultDescription += "".concat(err.message, " ");
            }
            logOp.resultSignature = "Issue retrieving from CDN or translating to ".concat(type);
            _this.closeDBAndEndLog(logOp, "retrieving ".concat(modelId));
            throw err;
        });
    };
    // Checks for idb compatibility given this bug https://bugzilla.mozilla.org/show_bug.cgi?id=1246615
    ModelDownloader.prototype.checkFirefoxCompatibility = function (firefoxIndex) {
        // Get version number for Firefox - 'Firefox/{versionNum}'
        var version = window.navigator.userAgent.substring(firefoxIndex + 8, firefoxIndex + 11);
        version.replace('.', '');
        // Error only occurs in older versions, so only need to check below v70
        if (parseInt(version, 10) <= 70) {
            return new Promise(function (resolve, reject) {
                // eslint-disable-next-line security/detect-non-literal-fs-filename
                var openRequest = window.indexedDB.open(this.dbName, this.dbVersion);
                openRequest.onsuccess = function () {
                    resolve(true);
                };
                openRequest.onerror = function (error) {
                    reject(error);
                    return true;
                };
            });
        }
        else {
            return Promise.resolve(true);
        }
    };
    ModelDownloader.prototype.isBrowserIdbCompatible = function () {
        var _a, _b;
        if (typeof Array === 'undefined' || typeof Array.from === 'undefined' || typeof Proxy === 'undefined'
            || typeof URL === 'undefined' || typeof URL.createObjectURL === 'undefined') {
            return Promise.resolve(false);
        }
        else if (typeof window !== 'undefined' && ((_a = window.navigator) === null || _a === void 0 ? void 0 : _a.userAgent)) {
            if (window.navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
                return window.indexedDB ? this.checkFirefoxCompatibility(window.navigator.userAgent.toLowerCase().indexOf('firefox')) : Promise.resolve(false);
            }
            else if (window.navigator.userAgent.toLowerCase().indexOf('safari') > -1
                && window.navigator.userAgent.toLowerCase().indexOf('chrome') <= -1) {
                if (this.checkIdbByFeatures) {
                    // Safari will not allow use of idb unless specific criteria are met
                    /**
                     * Must check browser is Safari before checking requestStorageAccess
                     * because some idb compatible browsers do not support requestStorageAccess (ex. chrome)
                     * but there are some Safari browsers that also do not support requestStorageAccess
                     * so without checking if browser is Safari, there's no way to know whether to resolve true or false
                     * based on if requestStorageAccess is supported
                     * */
                    if ((_b = window.document) === null || _b === void 0 ? void 0 : _b.hasStorageAccess) {
                        return window.document.hasStorageAccess().then(function () {
                            return true;
                        }).catch(function () {
                            return false;
                        });
                    }
                    else {
                        return Promise.resolve(false);
                    }
                }
                else {
                    return Promise.resolve(false);
                }
            }
            else {
                return Promise.resolve(true);
            }
        }
        else {
            return Promise.resolve(false);
        }
    };
    ModelDownloader.prototype.accessDB = function () {
        var _this = this;
        if (!this.accessDBPromise) {
            return this.callOpenDB();
        }
        else {
            return this.accessDBPromise.then(function (db) {
                return db.transaction('ALModels_os').done;
            }).then(function () {
                return _this.accessDBPromise;
            }).catch(function () {
                return _this.callOpenDB();
            });
        }
    };
    ModelDownloader.prototype.callOpenDB = function () {
        var _this = this;
        return this.accessDBPromise = openDB(this.dbName, this.dbVersion, {
            upgrade: function (db) {
                if (db && !db.objectStoreNames.contains(_this.osName)) {
                    db.createObjectStore(_this.osName, { keyPath: 'modelId' });
                }
            },
        });
    };
    ModelDownloader.prototype.endLog = function (logOp, task) {
        if (logOp.success) {
            logOp.resultDescription = "Success ".concat(task);
            Log.info(0x222c504f /* tag_8lfbp */, LogCategory.CoreDefault, logOp.stop());
        }
        else {
            logOp.resultDescription += "error ".concat(task);
            Log.error(0x222c5050 /* tag_8lfbq */, LogCategory.CoreDefault, logOp.stop());
        }
    };
    ModelDownloader.prototype.closeDBAndEndLog = function (logOp, task) {
        var _this = this;
        if (this.accessDBPromise) {
            this.accessDBPromise.then(function (db) {
                db.close();
            }).catch(function (err) {
                if (logOp.resultDescription !== "".concat(err.message, " ")) {
                    logOp.success = false;
                    logOp.resultDescription += "".concat(err.message ? err.message : 'Error closing db', " ");
                    logOp.resultSignature = 'Issue closing db';
                }
            }).then(function () {
                _this.accessDBPromise = undefined;
                _this.endLog(logOp, task);
            });
        }
        else {
            this.endLog(logOp, task);
        }
    };
    /**
     * Retrieves files from IDB or, if that does not work, CDN.
     * If error occurs but files are still retrieved, will record error but still resolve with files.
     * @param modelId
     * @param version
     *
     * @returns Model from IDB or CDN.
     */
    ModelDownloader.prototype.getIndexedDBFiles = function (modelId, version, logOp) {
        var _this = this;
        return this.isBrowserIdbCompatible().catch(function (err) {
            logOp.success = false;
            logOp.resultDescription += "".concat(err.message, " ");
            logOp.resultSignature = 'Issue determining idb compatibility';
            return false;
        }).then(function (canUseIdb) {
            if (!canUseIdb) {
                return _this.downloadFiles(modelId, version);
            }
            else {
                return _this.initPromise.catch(function (err) {
                    logOp.success = false;
                    logOp.resultDescription += "".concat(err.message, " ");
                    logOp.resultSignature = 'Issue initializing';
                }).then(function () {
                    return _this.getModel(modelId);
                }).then(function (modelObj) {
                    // Check if correct version of model
                    if (modelObj && modelObj.model && modelObj.version == version) {
                        return Promise.resolve(modelObj.model);
                    }
                    else {
                        return Promise.reject(); // Retrieve model from CDN in catch
                    }
                }).catch(function (err) {
                    if (err) {
                        if (logOp.resultDescription !== "".concat(err.message, " ")) {
                            logOp.success = false;
                            logOp.resultDescription += "".concat(err.message, " ");
                        }
                        logOp.resultSignature = 'Issue retrieving from DB';
                    }
                    return _this.downloadFiles(modelId, version);
                }).then(function (model) {
                    // Update the timesSinceUsage
                    return _this.putModel(modelId, model, version).then(function () {
                        return (model);
                    }).catch(function (err) {
                        // Error putting model in IDB
                        if (logOp.resultDescription !== "".concat(err.message, " ")) {
                            logOp.success = false;
                            logOp.resultDescription += "".concat(err.message, " ");
                        }
                        logOp.resultSignature = 'Issue adding to DB';
                        return (model);
                    });
                });
            }
        });
    };
    ModelDownloader.prototype.downloadFiles = function (modelId, version) {
        var _this = this;
        // Download catalog
        return fetchResource("".concat(this.baseURL, "/catalog_").concat(modelId, "_").concat(version, ".json")).then(function (response) {
            if (response.ok) {
                return response.text();
            }
            else {
                throw new Error("CDN download response status: ".concat(response.status));
            }
        }).then(function (catalog) {
            var catalogInfo = JSON.parse(catalog);
            // URL add-on to resources
            var middleURL = "".concat(catalogInfo.MajorVersion, ".").concat(catalogInfo.MinorVersion, "/ModelResources");
            var modelInfo = "".concat(modelId, "/").concat(version, "/");
            // Download each file from folder
            return Promise.all(catalogInfo.Resources.map(function (catalogElement) {
                if (catalogElement.Name.length > modelInfo.length && catalogElement.Name.substring(0, modelInfo.length) === modelInfo) {
                    catalogElement.Name = catalogElement.Name.substring(modelInfo.length);
                }
                return fetchResource("".concat(_this.baseURL, "/").concat(middleURL, "/").concat(modelInfo).concat(catalogElement.Name, ".").concat(catalogElement.Type)).then(function (response) {
                    if (response.ok) {
                        return response.blob();
                    }
                    else {
                        throw new Error("CDN download response status: ".concat(response.status));
                    }
                }).then(function (blobFile) {
                    return ["".concat(catalogElement.Name, ".").concat(catalogElement.Type), blobFile];
                });
            }));
        }).then(function (arr) {
            return new Map(arr);
        });
    };
    ModelDownloader.prototype.getModel = function (modelId) {
        var _this = this;
        return this.accessDB().then(function (db) {
            // Attempt to retrieve file from IndexedDB
            return db.get(_this.osName, modelId);
        });
    };
    // Used to put new models in & update times since usage
    ModelDownloader.prototype.putModel = function (modelId, model, version) {
        var _this = this;
        return this.accessDB().then(function (db) {
            return db.put(_this.osName, { modelId: modelId, model: model, version: version, timesSinceUsage: 0 });
        });
    };
    return ModelDownloader;
}());
export { ModelDownloader };
//# sourceMappingURL=model-downloader.js.map